/* eslint-disable no-mixed-operators */
import * as React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { ActionButton, Callout, ConstrainMode, IColumn, IconButton, IListProps, mergeStyleSets, Panel, PanelType, SelectionMode, ShimmeredDetailsList, Stack, Text, TextField, Toggle, TooltipHost } from "@fluentui/react";
import { IUserProfile } from "../../Auth/types";
import { confirmDialog, showDialog } from "../../Dialog/actions";
import AdminWrapper from "../AdminWrapper";
import LoadingDialog from "../../../core/components/LoadingDialog";
import { RootState } from "../../../store/store";
import RbLabel, { LabelSize } from "../../../bosch-react/components/label/RbLabel";
import DetailsListWrapper from "../../../core/components/DetailsListWrapper/DetailsListWrapper";
import Configuration from "../../../core/libraries/Configuration";
import Helper from "../../../core/libraries/Helper";
import Template from "../../../core/libraries/Template";
import RbTextField from "../../../bosch-react/components/text-field/RbTextField";
import RbLink from "../../../bosch-react/components/link/RbLink";
import RbButton, { ButtonSize, ButtonType } from "../../../bosch-react/components/button/RbButton";
import { addNotificationItem, deleteNotification, getAllNotificationItems, toggleNotificationAsync, updateNotification } from "../services";
import NoRecordsFound from "../../../core/components/NoRecordsFound";
import RbLoadingSpinner from "../../../bosch-react/components/loading-spinner/RbLoadingSpinner";

export interface ILink {
    hyperLink: string,
    replacedText: string,
}

export interface INotificationItem {
    title: string,
    message: string,
    links: ILink[],
    active: boolean,
    rowKey: string,
}

interface IProps {
    userProfile: IUserProfile | undefined,
    confirmDialog: typeof confirmDialog,
    showDialog: typeof showDialog,
    history: any,
    match: any,
}

interface IState {
    notificationItems: INotificationItem[] | null,
    currentItem: INotificationItem | null,
    currentLinkItem: ILink | null,
    isCallOutVisible: boolean,
    isEditing: boolean,
    isLoading: boolean,
}

class NotificationSetting extends React.Component<IProps, IState> {

    private _addLinkId: string = "add-link";
    private _labelId: string = "label-id";
    private _descId: string = "desc-id";


    constructor(props: IProps) {
        super(props);
        this.state = {
            notificationItems: null,
            currentItem: {
                title: "",
                message: "",
                links: [],
                active: false,
                rowKey: ""
            },
            isCallOutVisible: false,
            currentLinkItem: {
                hyperLink: "",
                replacedText: ""
            },
            isEditing: false,
            isLoading: false,
        }
    }
    async componentDidMount() {
        await this.handleProfileChanged();
        await this.fetchNotifications();

    }
    async componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
        if (prevProps.userProfile !== this.props.userProfile) {
            await this.handleProfileChanged();
        }
    }
    handleProfileChanged() {
        const { userProfile } = this.props
        if (!!userProfile) {
            if (userProfile.permissions?.isSuperAdmin === false) {
                this.props.confirmDialog(
                    "No Permission",
                    "You do not have permission to access this page.",
                    true,
                    () => {
                        window.location.href = process.env.REACT_APP_HOME!;
                    }
                );
            }
        }
    }

    shimmeredDetailsListProps: IListProps = {
        renderedWindowsAhead: 0,
        renderedWindowsBehind: 0,
    };

    columns: IColumn[] = [
        {
            key: 'title', name: 'Title', fieldName: 'Title',
            currentWidth: Helper.resizeColumnByScreenWidth(5),
            minWidth: Helper.resizeColumnByScreenWidth(5),
            isResizable: true,
            onRender: (item: INotificationItem) => {
                return (<RbLabel size={LabelSize.Small}>{item.title}</RbLabel>);
            }
        },
        {
            key: 'message', name: 'Message', fieldName: 'Message',
            currentWidth: Helper.resizeColumnByScreenWidth(30),
            minWidth: Helper.resizeColumnByScreenWidth(30),
            isResizable: true,
            onRender: (item: INotificationItem) => {
                return (<RbLabel size={LabelSize.Small}>{item.message}</RbLabel>);
            }
        },
        {
            key: 'links', name: 'Links', fieldName: 'Links',
            currentWidth: Helper.resizeColumnByScreenWidth(30),
            minWidth: Helper.resizeColumnByScreenWidth(30),
            isResizable: true,
            onRender: (item: INotificationItem) => {
                return (
                    item!.links.map((link, index) => this.renderLink(link, index, false))
                );
            }
        },
        {
            key: 'actions', name: 'Actions', fieldName: 'Actions',
            currentWidth: Helper.resizeColumnByScreenWidth(30),
            minWidth: Helper.resizeColumnByScreenWidth(30),
            isResizable: true,
            onRender: (item: INotificationItem) => {
                return (
                    <Stack horizontal>
                        <Toggle
                            styles={{ root: { margin: "0" } }}
                            inlineLabel
                            checked={item.active}
                            onText="On"
                            offText="Off"
                            onChange={() => {
                                this.activeBanner(item);
                            }}
                            role="checkbox"
                        />
                        <ActionButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Edit"
                            ariaLabel="Edit"
                            onClick={() => {
                                this.setState({
                                    currentItem: item,
                                    isEditing: true,
                                })
                            }}
                        />
                        <ActionButton
                            iconProps={{ iconName: 'Delete' }}
                            title="DeleteNotification"
                            ariaLabel="DeleteNotification"
                            onClick={() => { this.deleteNotification(item.rowKey) }}
                        />
                    </Stack>
                );
            }
        }
    ]

    async fetchNotifications() {
        const notificationItems = await getAllNotificationItems();
        const proceededItems = Array.isArray(notificationItems) && notificationItems.map((item: any) => {
            item.links = JSON.parse(item.links);
            return item;
        }) || [];
        proceededItems.length > 0 && this.setState({ notificationItems: proceededItems });
    }

    async activeBanner(item: INotificationItem) {
        this.props.confirmDialog(`Are you sure you want to ${item.active ? "deactivate" : "activate"} the banner?`,
            item.active ? "This action will close banner permanently." : "Please note that activating this notification will automatically deactivate all other notifications in the list.",
            false,
            async () => {
                this.props.showDialog(`Turning ${item.active ? "off" : "on"}...`);
                const success = await toggleNotificationAsync(item.rowKey, !item.active)
                this.props.showDialog(false)
                if (success) {
                    this.setState({
                        notificationItems: this.state.notificationItems!.map((notificationItem) => {
                            if (notificationItem.rowKey === item.rowKey) {
                                notificationItem.active = !item.active;
                            }
                            else {
                                notificationItem.active = false;
                            }
                            return notificationItem;
                        })
                    })
                }
                else this.props.confirmDialog("Failed to update", "Error", true);
            })
    }

    updateCurrentItem = (updatedItem: Partial<INotificationItem>) => {
        this.setState((prevState: IState) => {
            if (!prevState.currentItem) {
                console.error("No current item is selected to update.");
                return null;
            }

            // Merge the updates into the current item
            const updatedCurrentItem = {
                ...prevState.currentItem,
                ...updatedItem, // Apply only the changes provided in `updatedItem`
            };

            return {
                ...prevState,
                currentItem: updatedCurrentItem,
            };
        });
    };

    updateCurrentLinkItem = (updatedItem: Partial<ILink>) => {
        this.setState((prevState: IState) => {
            if (!prevState.currentLinkItem) {
                console.error("No current item is selected to update.");
                return null;
            }

            // Merge the updates into the current item
            const updatedCurrentLinkItem = {
                ...prevState.currentLinkItem,
                ...updatedItem, // Apply only the changes provided in `updatedItem`
            };

            return {
                ...prevState,
                currentLinkItem: updatedCurrentLinkItem,
            };
        });
    }

    async deleteNotification(rowKey: string) {
        this.props.confirmDialog("Are you sure you want to delete this notification?", "Delete Notification", false, async () => {
            this.props.showDialog("Deleting...");
            const success = await deleteNotification(rowKey);
            this.props.showDialog(false);
            if (success) {
                this.setState({
                    notificationItems: this.state.notificationItems!.filter(item => item.rowKey !== rowKey)
                })
            }
            else this.props.confirmDialog("Failed to delete", "Error", true);
        })

    }

    styles = mergeStyleSets({
        button: {
            width: 130,
        },
        callout: {
            width: 400,
            maxWidth: '90%',
            padding: '20px 24px',
        },
        title: {
            marginBottom: 12,
        },
        link: {
            display: 'block',
            marginTop: 20,
        },
    });

    renderTitle() {
        const { currentItem } = this.state;
        return Template.renderCommonTemplate(
            "Title",
            <TextField underlined required errorMessage={!currentItem?.title && "This field could not be empty" || undefined}
                onChange={(event: React.ChangeEvent<any>) => { this.updateCurrentItem({ title: event.currentTarget.value }); }}
                value={currentItem?.title} />,
            false, 2, 8
        );
    }

    renderLinks(showDelete: boolean = false) {
        const { currentItem } = this.state;
        return Template.renderCommonTemplate(
            "",
            currentItem!.links.map((link, index) => this.renderLink(link, index, showDelete))
            ,
            false, 2, 8
        );
    }

    renderLink = (link: ILink, index: number, showDelete: boolean) => {
        return <div key={index}>
            <div className="ms-Grid">
                <div className="ms-Grid-row">
                    <div className={`ms-Grid-col ms-sm${showDelete ? 10 : 12}`}>
                        <RbLabel size={LabelSize.Small}> {"Replaced Text: " + link.replacedText}</RbLabel>
                        <RbLink url={link.hyperLink} shouldOpenNewTab={true} size={LabelSize.Small} label={"Hyperlink: " + link.hyperLink}></RbLink>
                    </div>
                    {!!showDelete &&
                        <div className="ms-Grid-col ms-sm2">
                            <ActionButton
                                iconProps={{ iconName: 'Delete' }}
                                title="DeleteLink"
                                ariaLabel="DeleteLink"
                                onClick={() => {
                                    this.setState({
                                        currentItem: {
                                            ...this.state.currentItem!,
                                            links: this.state.currentItem!.links.filter((item, i) => i !== index)
                                        }
                                    })
                                }}
                            />
                        </div>
                    }
                </div>
            </div>

        </div>
    }

    renderMessage() {
        const { isCallOutVisible, currentItem } = this.state;
        return Template.renderCommonTemplate(
            "Message",
            <>
                <TextField underlined errorMessage={!currentItem?.message && "This field could not be empty" || undefined} required multiline
                    rows={20}
                    value={currentItem?.message}
                    onChange={(event: React.ChangeEvent<any>) => { this.updateCurrentItem({ message: event.currentTarget.value }); }} />
                <RbLink id={this._addLinkId} hasIcon={false} size={LabelSize.Small} label="Add link" onClick={() => { this.setState({ isCallOutVisible: true }) }} />
                {isCallOutVisible &&
                    <Callout
                        className={this.styles.callout}
                        ariaLabelledBy={this._labelId}
                        ariaDescribedBy={this._descId}
                        role="dialog"
                        gapSpace={0}
                        target={`#${this._addLinkId}`}
                        onDismiss={() => {
                            this.setState({ isCallOutVisible: false });
                        }}
                        setInitialFocus
                    >
                        <Text
                            className={this.styles.title}
                            as="h2" block variant="xLarge" id={this._labelId}>
                            Add Link
                        </Text>
                        <Text
                            as="p"
                            block variant="small" id={this._descId}>
                            The links will replaced with the Replaced Text in the message and display by the displayed text.
                        </Text>
                        <div className="ms-Grid">
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12">
                                    <RbTextField label="Hyperlink" onChange={(event: React.ChangeEvent<any>) => { this.updateCurrentLinkItem({ hyperLink: event.currentTarget.value }); }} />
                                    <RbTextField label="Replaced Text" onChange={(event: React.ChangeEvent<any>) => { this.updateCurrentLinkItem({ replacedText: event.currentTarget.value }); }} />
                                    <RbButton type={ButtonType.Primary} size={ButtonSize.SupperTiny} label="Add" onClick={() => { this.onAddLink() }} />
                                </div>
                            </div>
                        </div>
                    </Callout >

                }
            </>,
            false, 2, 8
        );
    }

    onAddLink() {
        this.updateCurrentItem({ links: [...this.state.currentItem?.links!, this.state.currentLinkItem!] });
        this.setState({ isCallOutVisible: false });
    }

    onDeleteLink() { }

    async onUpdateNotificationItem() {
        const { currentItem } = this.state
        const isValid = !!currentItem?.title && !!currentItem?.message;
        if (isValid) {
            const tempItem = { ...currentItem }
            this.props.showDialog("Updating...");
            const isSuccess = await updateNotification(tempItem);
            this.props.showDialog(false);
            this.props.confirmDialog(isSuccess ? "Successfully updated" : "Failed to update", isSuccess ? "Success" : "Error", true, () => {
                if (isSuccess) {
                    this.setState({
                        currentItem: { title: "", message: "", links: [], active: false, rowKey: "" },
                        notificationItems: this.state.notificationItems!.map(item => item.rowKey === currentItem!.rowKey ? tempItem : item)
                    });
                }
            });
        }
        else {
            this.props.confirmDialog("Please fill the required fields", "Title and Message are required", true);
        }
        this.setState({ isEditing: false });
    }

    async onAddNotification() {
        const { currentItem } = this.state
        const isValid = !!currentItem?.title && !!currentItem?.message;
        if (isValid) {
            currentItem.rowKey = Helper.generateGUID();
            this.props.showDialog("Adding...");
            const isSuccess = await addNotificationItem(currentItem);
            this.props.showDialog(false);
            this.props.confirmDialog(isSuccess ? "Successfully added" : "Failed to add", isSuccess ? "Success" : "Error", true, () => {
                if (isSuccess) {
                    this.setState({
                        currentItem: { title: "", message: "", links: [], active: false, rowKey: "" },
                        notificationItems: !this.state.notificationItems ? [currentItem] : [...this.state.notificationItems, currentItem]
                    });
                }
            });
        }
        else {
            this.props.confirmDialog("Please fill the required fields", "Title and Message are required", true);
        }
    }

    async onSaveNotification() {
        const { isEditing } = this.state
        if (isEditing) {
            this.onUpdateNotificationItem();
        }
        else {
            this.onAddNotification();
        }
    }

    render() {
        const { notificationItems, currentItem, isEditing } = this.state
        return !!this.props.userProfile && <AdminWrapper history={this.props.history}>
            <div className="ms-Grid">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm4">
                        <div className="ms-Grid">
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm12">
                                    <RbLabel className="header-title" size={LabelSize.Large}>
                                        Notification Setting
                                    </RbLabel>
                                    <TooltipHost
                                        content={"Manage notification content displayed as a banner when users access FEBER, ensuring important updates and messages are effectively communicated."}
                                        calloutProps={{ gapSpace: 10 }}
                                    >
                                        <IconButton
                                            iconProps={{ iconName: "Info" }}
                                            ariaLabel="Tab information"
                                            title="Tab information"
                                        />
                                    </TooltipHost>
                                    {this.renderTitle()}
                                    {this.renderMessage()}
                                    {!!currentItem && currentItem.links.length > 0 && this.renderLinks(true)}
                                </div>
                                <div className="ms-Grid-col ms-sm12">
                                    <RbButton label={!!isEditing ? "Save changes" : "Add new"} size={ButtonSize.Tiny} onClick={() => { this.onSaveNotification() }} />
                                    <RbButton label={"Clear"} type={ButtonType.Secondary} size={ButtonSize.Tiny} onClick={() => {
                                        this.setState({
                                            currentItem: { title: "", message: "", links: [], active: false, rowKey: "" },
                                            isEditing: false,
                                        })
                                    }} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="ms-Grid-col ms-sm8">
                        {this.state.isLoading && <div style={{ display: "block", width: "100%", height: "5rem", textAlign: "center" }}>
                            <RbLoadingSpinner />
                        </div> ||
                            <DetailsListWrapper>
                                {!notificationItems || notificationItems.length === 0 && <NoRecordsFound /> ||
                                    <ShimmeredDetailsList
                                        setKey="set"
                                        compact={true}
                                        items={notificationItems || []}
                                        constrainMode={ConstrainMode.unconstrained}
                                        columns={this.columns}
                                        enableShimmer={!notificationItems}
                                        selectionMode={SelectionMode.none}
                                        ariaLabelForShimmer="Content is being fetched"
                                        ariaLabelForGrid="Pending order details"
                                        listProps={this.shimmeredDetailsListProps}
                                        onRenderDetailsHeader={Configuration.onRenderStickyDetailsHeader}
                                        onShouldVirtualize={() => { return true }}
                                    />}
                            </DetailsListWrapper>
                        }
                    </div>
                </div>
            </div>
        </AdminWrapper> || <LoadingDialog />;
    }
}

const mapStateToProps = (state: RootState) => ({
    userProfile: state.auth.userProfile,
});

export default withRouter(connect(mapStateToProps, { confirmDialog, showDialog })(NotificationSetting));